import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'

import Logger from 'core/utils/Logger'

import { IJBPPlanning, IJBPPlanningItem, SMBFractionTypes } from '../../../models/IJBPPlanning'
import { P360_FIRST_FYEAR, PLAN_REQUEST_FOR_PREV_YEARS } from '../../config'

import { getEmptyPlanningItem } from './getEmptyPlanningItem'

export interface IPreparePlanningItems {
  planningSettings: IFormActualsTargetsSettings[]
  source: IJBPPlanning[]
  partnerId: string
  fyear: number
  lastFYear: number
  isActuals: number
  isDirect: number
  isTarget: number
  isQuota: number
  isComposedReport: boolean
  isNoSplit: number
  isRollUp: number
  isTotal: number
  isPartnerCount: number
  globalCategory?: string
  // SMB vs EU split: sp returns Total + SMB + EU rows with the same flags,
  // so the source has to be narrowed by the fraction to get a single row
  smbFraction?: SMBFractionTypes
}

/**
 * Amounts are expected to differ between duplicated rows and say nothing about why the sp
 * returned more than one of them, so they are left out of the diagnostics.
 */
const isFinancialOrSobField = (field: string) =>
  field.startsWith('SellThru') || field.startsWith('Contractual') || /sob/i.test(field)

/**
 * Explains why several source rows matched one planning cell: reports every field, apart from
 * the financial and SoB amounts, whose value is not the same across those rows. An empty
 * result means the duplicates are identical except for the amounts themselves.
 */
export const getDuplicatedDataDiff = (sourceData: IJBPPlanning[]): string => {
  if (sourceData.length < 2) {
    return ''
  }

  const rows = sourceData as unknown as Record<string, unknown>[]
  const fields = new Set<string>()
  rows.forEach((row) => Object.keys(row).forEach((field) => fields.add(field)))

  const format = (value: unknown) => (value === undefined ? 'undefined' : JSON.stringify(value))

  const differences = [...fields]
    .filter((field) => !isFinancialOrSobField(field))
    .sort()
    .reduce<string[]>((acc, field) => {
      const values = [...new Set(rows.map((row) => format(row[field])))]

      return values.length > 1 ? [...acc, `${field}: ${values.join(' | ')}`] : acc
    }, [])

  return differences.join(', ')
}

export const preparePlanningItems = ({
  planningSettings,
  source,
  partnerId,
  fyear,
  lastFYear,
  isActuals,
  isDirect,
  isTarget,
  isQuota,
  isComposedReport,
  isNoSplit,
  isRollUp,
  isTotal,
  isPartnerCount,
  globalCategory,
  smbFraction = SMBFractionTypes.Total,
}: IPreparePlanningItems): IJBPPlanningItem[] => {
  const items: IJBPPlanningItem[] = []

  const fromFYear = Math.max(fyear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR)

  planningSettings.forEach((setting) => {
    for (let fy = fromFYear; fy <= lastFYear; fy++) {
      // Quarter = 5 for FY data
      for (let fq = 1; fq <= 5; fq++) {
        const sourceData = source.filter(
          (el) =>
            // Filtered on Top level: PartnerId, ProgramTypes, Category, IsActuals, IsTarget, IsQuota
            el.ProgramTypes === setting.ProgramType &&
            el.Category === setting.Category &&
            el.SubCategory === setting.SubCategory &&
            el.L3Category === setting.L3Category &&
            el.FYear === fy &&
            el.FQuarter === fq &&
            !!el.IsDirect === !!isDirect &&
            (!isComposedReport || el.IsNoSplit === isNoSplit) &&
            !!el.IsRollUp === !!isRollUp &&
            !!el.IsTotal === !!isTotal &&
            !!el.IsPartnerCount === !!isPartnerCount &&
            (el.SMBFraction || SMBFractionTypes.Total) === smbFraction
        )

        // For debug source data from SP: Look for Total data
        // if (isTotal && sourceData.length > 1) {
        //   console.log(`(IsTotal) ${sourceData.length}`)
        // }

        if (sourceData.length >= 1) {
          if (sourceData.length > 1) {
            // For debug source data from SP: Error in data if sourceData.length > 1
            const total = sourceData.some((el) => !!el.IsTotal) ? ' (IsTotal)' : ''
            const smb = sourceData.some((el) => !!el.SMBFraction)
              ? `${sourceData[0].SMBFraction}`
              : '-'
            const duplicatedMessage = `Duplicated data (${sourceData.length})${total} differs by (SMBFraction: ${smb})`
            const duplicatedDiff = getDuplicatedDataDiff(sourceData)
            Logger.log(
              duplicatedDiff
                ? `${duplicatedMessage}: ${duplicatedDiff}`
                : `${duplicatedMessage} financial/SoB amounts only`
            )
          }

          const el = sourceData[0]

          const isDataPresent =
            !!el.SellThruSumLCNdp ||
            !!el.SellThruSumLCNet ||
            !!el.SellThruSumUSDNdp ||
            !!el.SellThruSumUSDNet ||
            !!el.SellThruUnits ||
            !!el.SellThruUnitsNdp ||
            !!el.ContractualSumUSDNet ||
            !!el.ContractualSumLCNet ||
            !!el.ContractualUnits ||
            !!el.ContractualSumUSDNdp ||
            !!el.ContractualSumLCNdp ||
            !!el.ContractualUnitsNdp

          const isAcknowledgedDataPresent =
            !!el.SellThruSumUSDNdpCompleted ||
            !!el.SellThruSumUSDNdpCompletedSob ||
            !!el.SellThruSumUSDNetCompleted ||
            !!el.SellThruSumUSDNetCompletedSob ||
            // !! el.SellThruUnitsNdpCompleted ||
            // !! el.SellThruUnitsCompleted ||
            !!el.ContractualSumUSDNdpCompleted ||
            !!el.ContractualSumUSDNdpCompletedSob ||
            !!el.ContractualSumUSDNetCompleted ||
            !!el.ContractualSumUSDNetCompletedSob
          // !! el.ContractualUnitsNdpCompleted ||
          // !! el.ContractualUnitsCompleted

          // Check if data is present in any of the fields
          // if (isDataPresent) {
          //   console.log(`DATA PRESENT ${sourceData.length}${isTotal ? ' (IsTotal)' : ''}`)
          // }

          items.push({
            ...el,

            IsDataPresent: isDataPresent,
            IsAcknowledgedDataPresent: isAcknowledgedDataPresent,

            IsChanged: 0,
            IsChangedSoB: 0,

            IsChangedSoBManual: 0,

            IsChangedManual: 0,
            IsChangedContractualManual: 0,
            IsChangedTransactionalManual: 0,

            IsChangedUnitsManual: 0,
            IsChangedUnitsContractualManual: 0,
            IsChangedUnitsTransactionalManual: 0,
          })
        } else {
          items.push(
            getEmptyPlanningItem({
              partnerId,
              fy,
              fq,
              setting,
              isActuals,
              isDirect,
              isTarget,
              isQuota,
              isNoSplit,
              isRollUp,
              isTotal,
              isPartnerCount,
              globalCategory,
              smbFraction,
            })
          )
        }
      }
    }
  })

  return items
}
